import { EventContextProvider } from "@codat/analytics";
import {
  Button,
  Card,
  Divider,
  InfoBanner,
  Modal,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { useSetIsDefault } from "api/public";
import { useProducts } from "contexts/ProductsContext";
import React, { useMemo, useState } from "react";
import formatUrl from "utils/formatUrl";

import { ProductCard } from "components/ProductCards";
import api from "config/urls/api";

import styles from "./ProductView.module.scss";

export const ProductView = () => {
  const { products, refetch: refetchProducts } = useProducts();
  const { addToast } = useToast();

  const [, updateIsDefault] = useSetIsDefault();

  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >();
  const [isDefault, setIsDefault] = useState(false);
  const [showDefaultWarningModal, setShowDefaultWarningModal] = useState(false);

  const handleIsDefaultChange = (
    productId: string | undefined,
    isDefault: boolean,
    ignoreRestrictions = false
  ) => {
    if (
      !ignoreRestrictions &&
      products?.filter((p) => p.isDefault).length === 1 &&
      !isDefault
    ) {
      setSelectedProductId(productId);
      setIsDefault(isDefault);
      setShowDefaultWarningModal(true);
      return;
    } else {
      setShowDefaultWarningModal(false);
    }
    updateIsDefault({
      url: formatUrl(api.products.one, { productId }),
      data: { isDefault },
    })
      .then(() => {
        refetchProducts();
      })
      .catch(() => {
        addToast({
          message: `Failed to update default status for ${productId}`,
          variant: "error",
        });
      });
  };

  const onCloseDefaultWarningModal = () => {
    setShowDefaultWarningModal(false);
  };

  const enabledProducts = useMemo(() => {
    return products.filter((product) => product.status === "Enabled");
  }, [products]);

  const hasEnabledProducts = enabledProducts.length > 0;

  return (
    <div className={styles.productView}>
      <div>
        <Typography as="h1" variant="heading_xl">
          Your Products
        </Typography>
        <Typography as="p" variant="body_md">
          Tailor your Codat experience to your business&apos; use case with our
          products
        </Typography>
        <Divider />
      </div>
      {!hasEnabledProducts && (
        <div>
          <InfoBanner
            className={styles.defaultWarning}
            color="warning"
            title="No products"
            subtitle="We couldn't find any products on your account. Contact your account manager"
            data-testid="no-products-warning-banner"
          />
        </div>
      )}
      {hasEnabledProducts &&
        enabledProducts?.filter((p) => p.isDefault).length === 0 && (
          <div>
            <InfoBanner
              className={styles.defaultWarning}
              color="warning"
              title="No default products"
              subtitle="You will need to specify a relevant product when creating new companies. You can add a product as a default below."
              data-testid="default-warning-banner"
            />
          </div>
        )}
      <div data-testid="enabled-products" className={styles.cards}>
        {enabledProducts.map((product) => {
          return (
            <ProductCard
              onChangeDefault={(isDefault) =>
                handleIsDefaultChange(product.productId, isDefault)
              }
              product={product}
              key={product.productId}
            />
          );
        })}
      </div>
      {showDefaultWarningModal && (
        <EventContextProvider name="Default Warning Modal">
          <Modal onClose={onCloseDefaultWarningModal}>
            <Card
              title="Warning"
              className={styles.defaultWarningModal}
              data-testid="default-warning-modal"
            >
              <Typography as="h2" variant="heading_lg">
                Are you sure you want to remove all default products?
              </Typography>
              <Typography as="p" variant="body_md">
                Default products are automatically enabled for new companies.
                With no default products set, no products will be enabled for
                new companies.
              </Typography>
              <div className={styles.modalButtons}>
                <Button
                  className={styles.confirm}
                  color="negative"
                  onClick={() =>
                    handleIsDefaultChange(selectedProductId, isDefault, true)
                  }
                  data-testid="default-warning-confirm"
                >
                  Confirm
                </Button>
                <Button
                  onClick={onCloseDefaultWarningModal}
                  data-testid="default-warning-cancel"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </Modal>
        </EventContextProvider>
      )}
    </div>
  );
};
